import { memo } from "react";

import { useGetAdhocQueryMetadataQuery } from "metabase/api";
import { useSnapshotSelector } from "metabase/common/hooks";
import * as Urls from "metabase/lib/urls";
import { getMetadata } from "metabase/selectors/metadata";
import { Box, Card, Loader, Stack } from "metabase/ui";
import Visualization from "metabase/visualizations/components/Visualization";
import * as Lib from "metabase-lib";
import { defaultDisplay } from "metabase-lib/query/display";
import type Metadata from "metabase-lib/v1/metadata/Metadata";
import type {
  CardDisplayType,
  Dataset,
  InspectorCard,
  InspectorCardDisplayType,
  RawSeries,
  VisualizationSettings,
} from "metabase-types/api";
import { createMockCard } from "metabase-types/api/mocks";

import { useLensCardLoader } from "../../../../hooks";
import { CardAlerts } from "../../../CardAlerts";
import { CardDrills } from "../../../CardDrills";
import { useLensContentContext } from "../../../LensContent/LensContentContext";

type VisualizationCardProps = {
  card: InspectorCard;
  height?: number;
};

const DEFAULT_HEIGHT = 235;

export const VisualizationCard = memo(
  ({ card, height = DEFAULT_HEIGHT }: VisualizationCardProps) => {
    const { alertsByCardId, drillLensesByCardId } = useLensContentContext();
    const { data, isLoading: isDataLoading } = useLensCardLoader({ card });

    const { isLoading: isMetadataLoading } = useGetAdhocQueryMetadataQuery(
      card.dataset_query,
    );

    const metadata = useSnapshotSelector(getMetadata, [isMetadataLoading]);

    if (card.display === "hidden") {
      return null;
    }

    const alerts = alertsByCardId[card.id] ?? [];
    const drillLenses = drillLensesByCardId[card.id] ?? [];

    const { displayType, displaySettings } = getDisplayConfig(
      metadata,
      card,
      isMetadataLoading,
    );

    const rawSeries = buildRawSeries(data, card, displayType, displaySettings);
    const isLoading = isMetadataLoading || isDataLoading;

    const questionUrl = rawSeries?.[0]?.card
      ? Urls.serializedQuestion(rawSeries[0].card)
      : undefined;

    const getHref = questionUrl ? () => questionUrl : undefined;

    const onChangeCardAndRun = questionUrl
      ? () => Urls.openInNewTab(questionUrl)
      : undefined;

    return (
      <Card p="md" shadow="none" withBorder>
        <Stack gap="sm">
          {isLoading || !rawSeries ? (
            <Stack gap="sm" align="center" justify="center" h={height}>
              <Loader size="sm" />
            </Stack>
          ) : (
            <Box h={height}>
              <Visualization
                rawSeries={rawSeries}
                showTitle={true}
                isDashboard={true}
                getHref={getHref}
                onChangeCardAndRun={onChangeCardAndRun}
              />
            </Box>
          )}

          <CardAlerts alerts={alerts} />

          <CardDrills drillLenses={drillLenses} />
        </Stack>
      </Card>
    );
  },
);

VisualizationCard.displayName = "VisualizationCard";

const getDisplayConfig = (
  metadata: Metadata,
  card: InspectorCard,
  isMetadataLoading: boolean,
) => {
  if (isMetadataLoading) {
    return { displayType: card.display, displaySettings: {} };
  }

  try {
    const query = Lib.fromJsQueryAndMetadata(metadata, card.dataset_query);
    const { display, settings = {} } = defaultDisplay(query);
    const finalDisplay =
      display === "table" || display === "bar" ? card.display : display;
    return { displayType: finalDisplay, displaySettings: settings };
  } catch {
    return { displayType: card.display, displaySettings: {} };
  }
};

const buildRawSeries = (
  dataset: Dataset | undefined,
  card: InspectorCard,
  displayType: InspectorCardDisplayType,
  displaySettings: Partial<VisualizationSettings>,
): RawSeries | undefined => {
  if (!dataset) {
    return;
  }

  const vizDisplay: CardDisplayType =
    displayType === "hidden" ? "table" : displayType;

  return [
    {
      card: createMockCard({
        name: card.title,
        display: vizDisplay,
        displayIsLocked: true,
        dataset_query: card.dataset_query,
        visualization_settings: {
          "graph.y_axis.labels_enabled": false,
          "graph.x_axis.labels_enabled": false,
          "table.row_index": false,
          ...displaySettings,
          ...card.visualization_settings,
        },
      }),
      data: dataset.data,
    },
  ];
};
